<template>
  <div class="pa-simple-text-editor">
    <form class="pa-form">
      <textarea
       :value="inputText"
       @input="handleInputTextarea"
       :placeholder="textareaPlaceholder"
       :disabled="isTextareaDisabled"
      ></textarea>
    </form>

    <div class="meta-field">
      <div class="attached-images" v-if="enableAttachImages">
        <ul class="images" :class="additionalImagesClass">
          <li  v-for="(item, index) in inputImagesDataURL" :key="index">
            <img
            :src="item"
            :class="additionalImagePreviewClass"
            />

            <div
            class="image-compression-loading"
            v-if="isImageExecuteRunning"
            >
              <ImageCompressionLoader
              width="25"
              height="25"
              viewBox="0 0 38 38"
              />
            </div>

            <div
            class="close-button"
            :class="additionalImagePreviewClass"
            @click.prevent="handleDeleteImage(index)"
            >
              <font-awesome-icon icon="times" />
            </div>
          </li>
        </ul>
      </div>

      <div class="controls">
        <div class="left-side">
          <div class="editor-expand" v-if="couldEditorExpand">
            <component :is="expandButtonIconName" @click.prevent="handleClickExpandIcon"></component>
          </div>
          <div 
           class="text-counter"
           :class="textCounterClass"
           v-if="shouldDisplayTextCounter"
          >{{inputCount}}/{{textMaximumSize}}</div>

          <PAInkCounterBanner
           v-if="shouldShowInkCounter"
           :textCounting="inkCountingParams.textCounting"
           :storedInkCount="inkCountingParams.storedInkCount"
           :unitLabel="inkCountingParams.unitLabel"
           :displayStyle="inkCountingParams.displayStyle"
           :popperPlacement="inkCountBannerPopperArrowPosition"
          />
        </div>
        <div class="right-side">
          <div
           class="attach-picture"
           :class="attachPictureButtonClass"
           @click.prevent="handleClickAttachPictureButton"
           v-if="enableAttachImages"
          >
            <font-awesome-icon :icon="['far', 'image']" />
          </div>

          <form
           method="POST"
           enctype="application/x-www-form-urlencoded"
           class="pa-form-file-hidden"
           v-if="enableAttachImages"
          >
            <input :disabled="isFileFieldDisabled" @change="handleChangeFileField" type="file" accept="image/*" ref="imageFileInput" />
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import IconExpand   from '@/components/Parts/PAConnectionEditor/Images/icon-expand.svg?inlineN'
import IconUnExpand from '@/components/Parts/PAConnectionEditor/Images/icon-unexpand.svg?inlineN'
import ImageCompressionLoader from '@/components/Parts/PAConnectionEditor/Images/tail-spin.svg?inlineF'

import PAInkCounterBanner from '@/components/Parts/PAInkCounterBanner/PAInkCounterBanner.vue'

import ImageUploadableMixins from '@/mixins/ImageUploadableMixins.js'

import CharacterInkCountableMixins from '@/mixins/CharacterInkCountableMixins.js'

export default {
  props : {
    inputText                 : {
      default: ''
    },

    inputImages              : {
      default: () => {
        return [];
      }
    },

    textareaPlaceholder       : {
      default: ''
    },

    textMaximumSize           : {
      default: 120
    },

    couldEditorExpand         : {
      default: true
    },

    enableAttachImages        : {
      default: true
    },

    shouldDisplayTextCounter  : {
      default: true
    },

    isEditorLoading           : {
      default: false
    },

    shouldShowInkCounter      : {
      default: false
    },

    inkCounterDisplayStyle    : {
      default: 'countingWithLimit'
    },
    
    inkCountBannerPopperArrowPosition : {
      default: 'top'
    }
  },

  components : {
    IconExpand,
    IconUnExpand,
    ImageCompressionLoader,
    PAInkCounterBanner
  },

  mixins : [
    ImageUploadableMixins,
    CharacterInkCountableMixins
  ],

  data(){
    return {
      isEditorExpand              : false,

      // Textarea
      // inputMessage                : '',
      isTextLimitOverd            : false,
      isTextareaDisabled          : false,

      // ExpandButtonImage
      expandButtonIconName        : 'IconExpand',

      // TextCounting
      inputCount                  : 0,
      textCounterClass            : {
        'text-counter-overd'  : false
      },

      // AttachPictureButton
      attachPictureButtonClass    : {
        'attach-picture-disabled' : false,
      },

      // FileField
      isFileFieldDisabled         : false,

      additionalImagesClass       : {
        'images-is-disabled'  : false
      },

      additionalImagePreviewClass : {
        'is-disabled' : false
      }
    };
  },

  watch : {
    inputText(value){
      if(typeof value === 'undefined') return;

      this.inputCount = value.length;

      if(this.inputCount <= this.textMaximumSize){
        this.isTextLimitOverd = false;
      }else{
        this.isTextLimitOverd = true;
      }

      this.$emit('changeText', value);
    },

    inputImages : {
      handler(newValue, oldValue){
        if((newValue.length === 0) && (oldValue.length > 0)){
          this.initializeImageUpload();
        }
      },
      deep: true
    },

    isTextLimitOverd(value){
      this.textCounterClass['text-counter-overd'] = value;

      this.$emit('changeTextLimitOverd', value);
    },

    couldAttachImages : {
      handler(value){
        this.attachPictureButtonClass['attach-picture-disabled'] = !value;

        this.isFileFieldDisabled = !value;
      },
      immediate: true
    },

    isImageExecuteRunning : {
      handler(value){
        this.additionalImagePreviewClass['is-disabled'] = value;
        this.isSubmitButtonDisabled = value;
      },
      immediate: true
    },

    isEditorExpand : {
      handler(value){
        if(value === true){
          this.expandButtonIconName = 'IconUnExpand';
        }else{
          this.expandButtonIconName = 'IconExpand';
        }

        this.$emit('changeEditorExpand', value);
      }
    },

    isEditorLoading : {
      handler(value){
        if(value === true){
          this.isTextareaDisabled     = true;
          
          this.couldAttachImages      = false;

          this.additionalImagesClass['images-is-disabled'] = true;
        }else{
          this.isTextareaDisabled     = false;
          
          if(this.inputImagesDataURL.length >= this.imageUploadLimit){
            this.couldAttachImages = false;
          }else{
            this.couldAttachImages = true;
          }

          this.additionalImagesClass['images-is-disabled'] = false;
        }
      },
      immediate: true
    },

    inkCounterDisplayStyle : {
      handler(value){
        this.inkCountingParams.displayStyle = value;
      },
      immediate: true
    }
  },

  methods : {
    handleDeleteImage(index){
      if(this.isEditorLoading === true) return;
      if(this.isImageExecuteRunning === true) return;

      this.executeDeleteImage(index);
    },

    handleClickExpandIcon(){
      this.isEditorExpand = !this.isEditorExpand;
    },

    handleClickAttachPictureButton(){
      if(this.couldAttachImages === false) return;

      this.$refs.imageFileInput.click();
    },

    handleInputTextarea(event){
      this.$emit('changeText', event.target.value);
    }
  },

  mounted(){
    this.initializeImageUpload();
  }
}
</script>

<style lang="scss">
@import "~bulma/sass/utilities/_all";
@import "@/assets/scss/_variables.scss";

.pa-simple-text-editor{
  flex-grow: 1;
  display:block;
  box-sizing: border-box;
  width: 98%;
  margin: 0 auto;
  border:4px solid #89620C;
  background-color: #DDCEB3;
  border-radius: 8px;
  position: relative;

  display:flex;
  flex-direction: column;

  height:100%;

  @media screen and (min-width: $desktop) {
    width:632px;
  }

  form.pa-form{
    display: block;

    flex-grow: 1;
    min-height: 6rem;
    height:100%;
    
    textarea{
      display:block;
      box-sizing: border-box;
      padding: .3rem;
      width:100%;
      height:100%;
      min-height:6rem;
      font-size:1.2rem;
      line-height: 1.4;
      background-color:inherit;
      border:0;
      color:#595552;
      font-family: 'mplus-1m-medium';
      letter-spacing: .5px;
      flex-grow: 1;

      &::placeholder{
        color:#B39E8F;
      }

      &:focus{
        outline:none;
      }

      &:disabled{
        opacity:.7;
      }
    }
  }

  .meta-field{
    flex-grow: 0;

    .attached-images{
      background-color:rgba(206, 187, 155, .3);
      padding:.8rem .5rem;

      ul.images{
        display:flex;
        flex-direction: row;
        justify-content: space-between;

        &-is-disabled{
          opacity: .5;
        }

        li{
          width:48%;
          background:#BFA77F;
          border-radius: 4px;
          position: relative;

          > img{
            width:100%;
            vertical-align: middle;
            border-radius: 4px;

            &.is-disabled{
              opacity:.6;
            }
          }

          .close-button{
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            top:.2rem;
            right:.2rem;
            font-size: .9rem;
            width:1.2rem;
            height:1.2rem;
            background-color:rgba(200, 200, 200, .8);
            // color:#868179;
            color:#4d4a45;
            border-radius: 50%;
            margin:0;
            padding:0;
            z-index:10;

            &.is-disabled{
              opacity:.5;
            }
          }

          .image-compression-loading{
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            position: absolute;
            top:0;
            left:0;
            margin:0;
            padding:0;
            width:100%;
            height:100%;
            margin:0;
            padding:0;
            z-index:9;
          }
        }
      }
    }

    .controls{
      background-color:rgba(200, 200, 200, .5);
      color:#868179;
      display: flex;
      flex-direction: row;
      border-radius:0 0 4px 4px;
      
      .left-side{
        width:80%;
        display:flex;
        flex-direction: row;

        .editor-expand{
          margin-top:.45rem;
          margin-left:.5rem;
          
          svg{
            width:1.1rem;
            height: 1.1rem;

            .a{
              fill:#868179;
            }
          }
        }

        .text-counter{
          margin-left:.5rem;
          font-size:1.2rem;
          line-height: 1.7;

          &-overd{
            color:#cf4444;
          }
        }
      }

      .right-side{
        width:20%;
        text-align: right;
        display:flex;
        flex-direction: row-reverse;
        .attach-picture{
          z-index: 1;

          svg{
            margin-top:.2rem;
            margin-right:.5rem;
            width:1.5rem;
            height:1.5rem;
          }

          &-disabled{
            svg{
              path{
                fill:#B3AA9D;
              }
            }
          }
        }

        .pa-form-file-hidden{
          opacity: 0;
          width:0.1px;
        }
      }
    }
  }
}
</style>